import { useState, useEffect, useMemo } from 'react';
import { Table, Input, Button, Spin, Alert, Select } from 'antd';
import { SearchOutlined, FilterOutlined, ClearOutlined, WarningOutlined } from '@ant-design/icons';
import '../Reports.css';

const Highlight = ({ text, query }) => {
  const escHtml = s => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  if (!query || !text) return <span dangerouslySetInnerHTML={{ __html: escHtml(text) }} />;
  const esc = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${esc})`, 'gi'));
  const html = parts.map((p, i) =>
    i % 2 === 1
      ? `<span style="background-color:#e8b400">${escHtml(p)}</span>`
      : escHtml(p)
  ).join('');
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
};

const APCComponent = () => {
  const [journals, setJournals]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filterEditors, setFilterEditors] = useState([]);

  useEffect(() => {
    fetch('/data/apc.json')
      .then(r => r.json())
      .then(data => { setJournals(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return journals.filter(j => {
      if (q && !(
        (j.titulo  || '').toLowerCase().includes(q) ||
        (j.issn    || '').toLowerCase().includes(q) ||
        (j.editor  || '').toLowerCase().includes(q)
      )) return false;

      const edBase = (j.editor || '').replace(/\s*\([^)]*\)$/, '').trim();
      if (filterEditors.length && !filterEditors.includes(edBase)) return false;

      return true;
    });
  }, [journals, search, filterEditors]);


  const editorOptions = useMemo(() => {
    const names = [...new Set(journals.map(j => (j.editor || '').replace(/\s*\([^)]*\)$/, '').trim()))].filter(Boolean).sort();
    return names.map(n => ({ label: n, value: n }));
  }, [journals]);

  const hasFilters = filterEditors.length > 0;

  const clearFilters = () => { setFilterEditors([]); };

  const columns = [
    {
      title: 'TÍTULO', key: 'titulo',
      sorter: (a, b) => (a.titulo || '').localeCompare(b.titulo || '', 'es'),
      render: (_, r) => {
        const text = <Highlight text={r.titulo} query={search} />;
        if (r.url) return (
          <a href={r.url} target="_blank" rel="noopener noreferrer"
             style={{ fontSize: 13, fontWeight: 500, color: '#007398' }}
             onClick={e => e.stopPropagation()}>{text}</a>
        );
        return <span style={{ fontSize: 13, fontWeight: 500, color: '#007398' }}>{text}</span>;
      },
    },
    {
      title: 'ISSN', key: 'issn', width: 110,
      render: (_, r) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: r.issn ? '#555' : '#bbb' }}>
          {r.issn ? <Highlight text={r.issn} query={search} /> : '—'}
        </span>
      ),
    },
    {
      title: 'EDITOR', key: 'editor', width: 200,
      sorter: (a, b) => (a.editor || '').localeCompare(b.editor || '', 'es'),
      render: (_, r) => (
        <span style={{ fontSize: 12, color: '#555' }}>
          <Highlight text={(r.editor || '').replace(/\s*\([^)]*\)$/, '').trim()} query={search} />
        </span>
      ),
    },
    {
      title: 'MODELO', key: 'modelo', width: 170,
      render: (_, r) => {
        const m = r.modelo;
        if (!m || m === 'nan') return <span style={{ color: '#bbb' }}>—</span>;
        const colors = { 'Hybrid': 'blue', 'Fully open access': 'green', 'Full': 'green', 'Subsidized': 'purple', 'Conferences': 'orange' };
        return <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: colors[m] === 'green' ? '#f6ffed' : colors[m] === 'blue' ? '#e6f4ff' : '#f0f0f0', color: colors[m] === 'green' ? '#389e0d' : colors[m] === 'blue' ? '#0958d9' : '#555', border: `1px solid ${colors[m] === 'green' ? '#b7eb8f' : colors[m] === 'blue' ? '#91caff' : '#d9d9d9'}` }}>{m}</span>;
      },
    },
    {
      title: 'PRECIO', key: 'precioEUR', width: 120, align: 'right',
      sorter: (a, b) => (a.precioEUR ?? 0) - (b.precioEUR ?? 0),
      render: (_, r) => (
        <span style={{ fontWeight: 600, fontSize: 13, color: r.precioEUR > 0 ? '#007398' : '#bbb' }}>
          {r.precioEUR > 0 ? r.precioEUR.toLocaleString('de-DE') + ' €' : '—'}
        </span>
      ),
    },
  ];

  const tableComponents = {
    header: { cell: (props) => <th {...props} style={{ ...props.style, background: '#f0f5ff', color: '#333', fontWeight: 600, fontSize: 11, letterSpacing: '0.5px' }} /> },
  };

  return (
    <div style={{ textAlign: 'left' }}>

      {/* Header */}
      <div style={{ paddingTop: 20, paddingBottom: 4, textAlign: 'center', background: '#fafafa' }}>
        <div style={{
          fontSize: 22, fontWeight: 700, color: '#1a1a1a',
          letterSpacing: -0.3, lineHeight: 1.3,
          fontFamily: "'Inter','Segoe UI',sans-serif",
          padding: '0 24px',
        }}>
          Precios públicos APC (Article Processing Charges)
        </div>
        <div style={{ fontSize: 13, color: '#888', marginTop: 6, paddingBottom: 16 }}>
          Tarifas publicadas por editores para 2026 · Importes convertidos a euros
        </div>
      </div>

      {/* Banner en construcción */}
      <div style={{ padding: '0 48px 16px' }}>
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={<strong>Sección en construcción</strong>}
          description="Los datos de precios APC están siendo completados y verificados. Algunos editores pueden tener información incompleta (ISSNs o precios pendientes de actualización)."
          style={{ borderRadius: 8, fontSize: 13 }}
        />
      </div>

      {/* Panel de filtros */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 48px 0' }}>
        <div style={{ background: '#f8fafc', border: '1px solid #e8ecf0', borderRadius: 10, padding: '16px 20px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <FilterOutlined style={{ color: '#888' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Filtros</span>
            {hasFilters && (
              <Button size="small" icon={<ClearOutlined />} onClick={clearFilters} type="link" style={{ marginLeft: 'auto', fontSize: 12, color: '#e74c3c', padding: '0 4px' }}>
                Limpiar filtros
              </Button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 600 }}>BÚSQUEDA</div>
              <Input
                size="middle"
                placeholder="Título, ISSN o editor…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                allowClear
                style={{ borderRadius: 7, fontSize: 13, width: '100%' }}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 600 }}>EDITOR</div>
              <Select
                mode="multiple"
                allowClear
                placeholder="Todos los editores"
                value={filterEditors}
                onChange={setFilterEditors}
                options={editorOptions}
                style={{ width: '100%' }}
                size="middle"
                maxTagCount={2}
                showSearch
              />
            </div>

          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ padding: '0 48px 40px', maxWidth: 1280, margin: '0 auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <span className="journals-result-count">
                {visible.length.toLocaleString('de-DE')} revistas encontradas
              </span>
            </div>
            <Table
              columns={columns}
              dataSource={visible}
              rowKey={(r, i) => `${r.issn || r.titulo}-${i}`}
              components={tableComponents}
              size="middle"
              bordered={false}
              rowClassName={(_, i) => i % 2 === 0 ? '' : 'apc-row-stripe'}
              pagination={{
                pageSize: 50,
                showTotal: t => t.toLocaleString('de-DE') + ' resultados',
                showSizeChanger: false,
              }}
              locale={{ emptyText: 'No se encontraron revistas' }}
            />
            <div style={{ marginTop: 14, textAlign: 'right', fontSize: 11, color: '#aaa' }}>
              Fuentes: Elsevier · Springer Nature · Wiley · Sage · IEEE · MDPI · Frontiers
              {' · '}Tipos de cambio orientativos (1 USD ≈ 0,90 € · 1 GBP ≈ 1,14 € · 1 CHF ≈ 1,04 €)
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default APCComponent;
